import React, { useState, useEffect, useMemo } from 'react';
import { Save, Wallet } from 'lucide-react';
import { AppShell } from '../components/layout/AppShell';
import { PageHeader } from '../components/layout/PageHeader';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardBody, CardHeader } from '../components/ui/card';
import { PeriodStepper } from '../components/ui/period-stepper';
import { Skeleton } from '../components/ui/skeleton';
import { MonthEarningsField } from '../components/tax/MonthEarningsField';
import { OtherIncomeCard } from '../components/tax/OtherIncomeCard';
import { TaxSummaryCard } from '../components/tax/TaxSummaryCard';
import { formatCurrency } from '../lib/format';
import { ExpensesCard } from '../components/tax/expenses/ExpensesCard';
import { useTaxExpensesQuery } from '../hooks/useTaxExpensesQuery';
import {
  calculateTaxYear,
  formatTaxYear,
  getNextTaxYear, 
  getPreviousTaxYear,
  parseTaxYear
} from '../utils/taxUtils';
import { useTaxEarningsQuery, useUpsertTaxEarningsForYearMutation, useGetMonthlyEarningsFromTimeEntries } from '../hooks/useTaxEarningsQuery';

// UK tax year months: April (4) to March (3)
const TAX_YEAR_MONTHS = [
  { month: 4, name: 'April' },
  { month: 5, name: 'May' },
  { month: 6, name: 'June' },
  { month: 7, name: 'July' },
  { month: 8, name: 'August' },
  { month: 9, name: 'September' },
  { month: 10, name: 'October' },
  { month: 11, name: 'November' },
  { month: 12, name: 'December' },
  { month: 1, name: 'January' },
  { month: 2, name: 'February' },
  { month: 3, name: 'March' },
];

export const TaxCalculatorPage: React.FC = () => {
  // Get current tax year (UK tax year starts April 6)
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const defaultTaxYearStart = currentMonth < 4 ? currentYear - 1 : currentYear;
  const defaultTaxYear = formatTaxYear(defaultTaxYearStart);

  const [taxYear, setTaxYear] = useState(defaultTaxYear);
  const [hasOtherJob, setHasOtherJob] = useState(false);
  const [otherJobSalary, setOtherJobSalary] = useState('');
  const [isPaye, setIsPaye] = useState(false);
  const [monthlyEarnings, setMonthlyEarnings] = useState<Record<number, string>>({});

  const taxYearStart = parseTaxYear(taxYear);
  const { data: expenses = [] } = useTaxExpensesQuery(taxYearStart);
  const totalExpenses = useMemo(() => expenses.reduce((sum, e) => sum + e.amount, 0), [expenses]);
  const { data: taxEarningsData, isLoading } = useTaxEarningsQuery(taxYearStart);
  const saveYearMutation = useUpsertTaxEarningsForYearMutation();
  const fetchFromTimeEntriesMutation = useGetMonthlyEarningsFromTimeEntries();

  // Auto-fetch and initialize monthly earnings from database when tax year or data changes
  useEffect(() => {
    if (!isLoading) {
      if (taxEarningsData) {
        // Load monthly earnings from database
        const earnings: Record<number, string> = {};
        const savedEarnings = taxEarningsData.monthly_earnings || {};
        
        // Check if saved earnings are empty or all zero
        const hasEarnings = Object.values(savedEarnings).some(v => {
          const num = typeof v === 'number' ? v : parseFloat(v as string);
          return !isNaN(num) && num > 0;
        });

        if (hasEarnings) {
          // Use saved earnings
          Object.entries(savedEarnings).forEach(([monthStr, amount]) => {
            const month = parseInt(monthStr, 10);
            if (!isNaN(month) && (typeof amount === 'number' || typeof amount === 'string')) {
              const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
              if (!isNaN(numAmount)) {
                earnings[month] = numAmount.toString();
              }
            }
          });
          setMonthlyEarnings(earnings);
        } else {
          // Saved earnings are empty/zero - fetch from time entries
          fetchFromTimeEntriesMutation.mutate(taxYearStart, {
            onSuccess: (timeEntriesEarnings) => {
              const fetchedEarnings: Record<number, string> = {};
              Object.entries(timeEntriesEarnings).forEach(([monthStr, amount]) => {
                const month = parseInt(monthStr, 10);
                if (!isNaN(month) && typeof amount === 'number' && amount > 0) {
                  fetchedEarnings[month] = amount.toString();
                }
              });
              setMonthlyEarnings(fetchedEarnings);
            },
            onError: () => {
              // If fetching from time entries fails, just clear earnings
              setMonthlyEarnings({});
            }
          });
        }

        // Load other job information
        setHasOtherJob(taxEarningsData.has_other_job || false);
        setOtherJobSalary(taxEarningsData.other_job_salary ? taxEarningsData.other_job_salary.toString() : '');
        setIsPaye(taxEarningsData.is_paye || false);
      } else {
        // No data exists for this tax year - try to fetch from time entries
        fetchFromTimeEntriesMutation.mutate(taxYearStart, {
          onSuccess: (timeEntriesEarnings) => {
            const earnings: Record<number, string> = {};
            Object.entries(timeEntriesEarnings).forEach(([monthStr, amount]) => {
              const month = parseInt(monthStr, 10);
              if (!isNaN(month) && typeof amount === 'number' && amount > 0) {
                earnings[month] = amount.toString();
              }
            });
            setMonthlyEarnings(earnings);
          },
          onError: () => {
            // If fetching from time entries fails, just clear earnings
            setMonthlyEarnings({});
          }
        });
        
        // Clear other job information
        setHasOtherJob(false);
        setOtherJobSalary('');
        setIsPaye(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taxEarningsData, isLoading, taxYearStart]);

  const handlePreviousTaxYear = () => {
    setTaxYear(getPreviousTaxYear(taxYear));
  };

  const handleNextTaxYear = () => {
    setTaxYear(getNextTaxYear(taxYear));
  };

  const handleEarningsChange = (month: number, value: string) => {
    setMonthlyEarnings(prev => ({
      ...prev,
      [month]: value
    }));
  };

  const handleSaveAllEarnings = async () => {
    // Convert string values to numbers
    const earningsToSave: Record<number, number> = {};
    TAX_YEAR_MONTHS.forEach(({ month }) => {
      const value = monthlyEarnings[month] || '0';
      earningsToSave[month] = parseFloat(value) || 0;
    });

    try {
      await saveYearMutation.mutateAsync({
        taxYearStart,
        monthlyEarnings: earningsToSave,
        hasOtherJob,
        otherJobSalary: parseFloat(otherJobSalary) || 0,
        isPaye
      });
    } catch (error) {
      console.error('Failed to save earnings:', error);
    }
  };

  // Calculate annual earnings and tax for the tax year
  const taxYearCalculation = useMemo(() => {
    // Sum all monthly earnings to get annual total
    const totalAnnualEarnings = TAX_YEAR_MONTHS.reduce((sum, { month }) => {
      const value = monthlyEarnings[month] || '0';
      return sum + (parseFloat(value) || 0);
    }, 0);

    if (totalAnnualEarnings <= 0) {
      return null;
    }

    return calculateTaxYear({
      taxYear,
      earnings: totalAnnualEarnings,
      expenses: totalExpenses,
      hasOtherJob,
      otherJobSalary: parseFloat(otherJobSalary) || 0,
      isPaye,
    });
  }, [monthlyEarnings, totalExpenses, hasOtherJob, otherJobSalary, isPaye, taxYear]);

  // Each month's share of the annual Tax + NI bill, in proportion to what it earned.
  const annualTaxBill = taxYearCalculation ? taxYearCalculation.incomeTax + taxYearCalculation.nationalInsurance : 0;
  const setAsideFor = (monthlyAmount: number): number | null =>
    taxYearCalculation && annualTaxBill > 0 && monthlyAmount > 0 && taxYearCalculation.annualEarnings > 0
      ? (annualTaxBill * monthlyAmount) / taxYearCalculation.annualEarnings
      : null;

  const hasChanges = useMemo(() => {
    // Check if current earnings differ from saved data
    if (isLoading || !taxEarningsData) {
      return Object.values(monthlyEarnings).some(v => v && parseFloat(v) > 0) ||
             hasOtherJob ||
             (otherJobSalary && parseFloat(otherJobSalary) > 0) ||
             isPaye;
    }
    
    const savedEarnings = taxEarningsData.monthly_earnings || {};
    
    // Check if monthly earnings changed
    const earningsChanged = TAX_YEAR_MONTHS.some(({ month }) => {
      const current = parseFloat(monthlyEarnings[month] || '0') || 0;
      const saved = savedEarnings[month] || 0;
      return Math.abs(current - saved) > 0.01; // Allow for floating point precision
    });

    // Check if other job fields changed
    const otherJobChanged = 
      (hasOtherJob !== (taxEarningsData.has_other_job || false)) ||
      (Math.abs(parseFloat(otherJobSalary) - (taxEarningsData.other_job_salary || 0)) > 0.01) ||
      (isPaye !== (taxEarningsData.is_paye || false));

    return earningsChanged || otherJobChanged;
  }, [monthlyEarnings, taxEarningsData, isLoading, hasOtherJob, otherJobSalary, isPaye]);

  const handleHasOtherJobChange = (checked: boolean) => {
    setHasOtherJob(checked);
    if (!checked) {
      setOtherJobSalary('');
      setIsPaye(false);
    }
  };

  const enteredTotal = TAX_YEAR_MONTHS.reduce(
    (sum, { month }) => sum + (parseFloat(monthlyEarnings[month] || '0') || 0),
    0
  );

  return (
    <AppShell>
      <PageHeader
        eyebrow="UK · Self-employed"
        title="Tax estimate"
        description="Estimate your Income Tax and Class 4 National Insurance from what you've earned across the tax year."
        actions={
          <PeriodStepper
            label={`Tax year ${taxYear}`}
            onPrevious={handlePreviousTaxYear}
            onNext={handleNextTaxYear}
            previousLabel="Previous tax year"
            nextLabel="Next tax year"
          />
        }
      />

      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-[minmax(0,1fr)_360px] xl:grid-cols-[minmax(0,1fr)_390px] 2xl:grid-cols-[minmax(0,1fr)_440px]">
        <div className="flex min-w-0 flex-col gap-5">
          <Card>
            <CardHeader
              icon={<Wallet />}
              title="Monthly earnings"
              description={
                <>
                  April to March ·{' '}
                  <span className="numeric text-fg-muted">{formatCurrency(enteredTotal)}</span> entered
                </>
              }
              actions={
                <>
                  {hasChanges && !isLoading && <Badge tone="brand">Unsaved changes</Badge>}
                  <Button
                    size="sm"
                    onClick={handleSaveAllEarnings}
                    disabled={!hasChanges || saveYearMutation.isPending || isLoading}
                  >
                    <Save />
                    {saveYearMutation.isPending ? 'Saving…' : 'Save'}
                  </Button>
                </>
              }
            />
            <CardBody>
              {isLoading ? (
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                  {TAX_YEAR_MONTHS.map(({ month }) => (
                    <Skeleton key={month} className="h-[86px] rounded-xl" />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 xl:grid-cols-3">
                  {TAX_YEAR_MONTHS.map(({ month, name }) => {
                    const value = monthlyEarnings[month] || '';
                    return (
                      <MonthEarningsField
                        key={month}
                        month={month}
                        name={name}
                        value={value}
                        setAside={setAsideFor(parseFloat(value) || 0)}
                        onChange={(next) => handleEarningsChange(month, next)}
                      />
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>

          <ExpensesCard
            taxYearStart={taxYearStart}
            taxSaved={taxYearCalculation?.deductionType === 'expenses' ? taxYearCalculation.taxSavedByDeduction : 0}
            usingTradingAllowance={taxYearCalculation?.deductionType === 'trading-allowance'}
          />

          <OtherIncomeCard
            hasOtherJob={hasOtherJob}
            onHasOtherJobChange={handleHasOtherJobChange}
            otherJobSalary={otherJobSalary}
            onOtherJobSalaryChange={setOtherJobSalary}
            isPaye={isPaye}
            onIsPayeChange={setIsPaye}
          />
        </div>

        <aside className="lg:sticky lg:top-24">
          <TaxSummaryCard
            taxYear={taxYear}
            result={taxYearCalculation}
            otherIncomeNotIncluded={hasOtherJob && !isPaye && (parseFloat(otherJobSalary) || 0) > 0}
          />
        </aside>
      </div>
    </AppShell>
  );
};
